"use client";
import Link from "next/link";

import Form from "@/components/molecules/Form";
import { useLoginUser } from "@/lib/hooks/useSelection";
import textconfig from "@/textconfig";

import CartNotification from "@/components/molecules/CartNotification/CartNotification";
import { AxiosError } from "axios";
import styles from "../../guest.module.css";

const Messages = {
  WrongEmailFormat: "Vennligst skriv inn e-post med riktig format.",
  DefaultError:
    "Noe gikk galt med å gjenopprette passordet ditt. Vennligst prøv på nytt. Dersom problemet vedvarer, kontakt oss på nettbutikk@raumaull.no.",
  SuccessMessage:
    "Vi har sendt deg en lenke på e-post hvor du kan gjenopprette passordet ditt. Hvis du ikke ser e-posten fra oss innen et par minutter, anbefaler vi at du sjekker søppelpost.",
  WrongEmailOrPassord: "Feil e-post eller passord. Prøv igjen.",
} as const;

export const ClientLogInForm = ({
  loginFunction,
  options = true,
}: {
  loginFunction: {
    mutate: (data: { email: string; password: string }) => void;
  };
  options?: boolean;
}) => {
  const { buttonLabel, forgotPasswordLabel } = textconfig.login;
  return (
    <Form.Root
      onSubmit={(data) => {
        loginFunction.mutate({
          email: data.email as string,
          password: data.password as string,
        });
      }}
    >
      <div className={styles.inputs}>
        <Form.EmailField />
        <Form.PasswordField />
      </div>
      <Form.Submit className={styles.button}>{buttonLabel}</Form.Submit>
      {options && (
        <div className={styles.options}>
          <Form.Checkbox id="remember" label="Husk meg" />
          <Link href="/glemt-passord">{forgotPasswordLabel}</Link>
        </div>
      )}
    </Form.Root>
  );
};

const ClientLogIn = () => {
  const { login: loginFunction } = useLoginUser();

  const signupError = loginFunction.error as AxiosError<{
    errors: { email: string; password: string };
  }>;
  const isWrongEmailOrPassord =
    signupError?.response?.data?.errors?.password === "incorrect";
  const isWrongEmailFormat =
    signupError?.response?.data?.errors?.email === "expected e-mail address";

  const { title, subtitle, registerButtonLabel } = textconfig.login;
  return (
    <div className={styles.page} data-layout="page">
      <section className={styles.section}>
        <h1>{title}</h1>
        <ClientLogInForm loginFunction={loginFunction} />
        {!!isWrongEmailFormat && (
          <div className={styles.signupNotifications}>
            <CartNotification
              type="error"
              message={Messages.WrongEmailFormat}
            />
          </div>
        )}
        {!!isWrongEmailOrPassord && (
          <div className={styles.signupNotifications}>
            <CartNotification
              type="info"
              message={Messages.WrongEmailOrPassord}
            />
          </div>
        )}
        {!!signupError && !isWrongEmailOrPassord && !isWrongEmailFormat && (
          <div className={styles.signupNotifications}>
            <CartNotification type="error" message={Messages.DefaultError} />
          </div>
        )}
        <div className={styles.divider} aria-hidden />
        <h2 className={styles.subtitle}>{subtitle}</h2>

        <Link href={"/registrer"} className={styles.registerButton}>
          <button type="button" className={styles.button}>
            {registerButtonLabel}
          </button>
        </Link>
      </section>
    </div>
  );
};

export default ClientLogIn;
